import { Component, OnDestroy, OnInit, AfterViewChecked} from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { DatePipe } from '@angular/common';
import { Observable } from 'rxjs/Observable';
import { Subscription } from 'rxjs/Subscription';
import { IMultiSelectOption } from 'angular-2-dropdown-multiselect';
import { BsDatepickerConfig } from 'ngx-bootstrap/datepicker';

import { AuthenticationService } from '../shared/authentication.service';
import { CivilCase } from './civil-case'; // TODO  remove this when development is complete.  Ron
import { CivilIdentitySearchCase } from './civil-identity-search-case';
import { CivilIdentitySearchResponse } from './civil-identity-search-response';
import { CivilSearchConstants } from './civil-search-constants';
import { CivilSearchService } from './civil-search.service';
import { CountyService } from '../shared/county.service';
import { CourtCase } from './../shared/court-case';
import { EventService } from './../shared/event.service';

import { environment } from './../../environments/environment';
import { CaseIdentifier } from '../shared/case-identifier';
import { asyncRequired, asyncPattern, asyncFromGreater, asyncMinInt, asyncMaxInt } from '../shared/field-validators';
import { CivilIdentiyAdditionalDetails } from './civil-identity-additional-details';
import { CivilIdentityOtherData } from './civil-identity-other-data';
import { CivilIdentityAdditionalData } from './civil-identity-additional-data';
import { Subject } from 'rxjs';
import { FileNumberUtil } from '../shared/file-number-util';
import { TimepickerModule } from 'ngx-bootstrap';
import { callNgModuleLifecycle } from '@angular/core/src/view/ng_module';

enum AccordionType {
  Case_Number = 'CASENUMBER',
  Party_Name = 'PARTY NAME'
}
enum PartyNameSearchResultTab {
  Cases = 'Cases',
  Indexed_Judgments = 'Indexed Judgments'
}
enum SearchType {
  Case_Lookup = 'Case Lookup',
  Party_Name_Cases = 'Party Name Cases',
  Party_Name_Indexed_Judgments = 'Party Name Indexed Judgments'
}

@Component({
  selector: 'app-civil',
  templateUrl: './civil.component.html',
  styleUrls: ['./civil.component.less']
})
export class CivilComponent implements OnDestroy, OnInit, AfterViewChecked {

  submitted = false;
  errors: string[] = [];
  civilSearchFormGroup: FormGroup;
  openAccordion: AccordionType = AccordionType.Party_Name;
  lastSubmittedSearchType: SearchType;
  activeSearchResultTab: PartyNameSearchResultTab;
  requestSubscription: Subscription;
  otherDataRequestSubscription: Subscription;

  // for use in the templates
  enums = {
    AccordionType: AccordionType,
    PartyNameSearchResultTab: PartyNameSearchResultTab,
    SearchType: SearchType
  }

  searchCriteriaDescription: string;
  counties: IMultiSelectOption[] = this.buildIMultiSelectOptions(CountyService.getCounties());
  searchModes: string[] = [
    'Exact Match',
    'Begins with'
  ];
  caseTypes: IMultiSelectOption[] = CivilSearchService.getCaseTypes(null);
  issueTypes: IMultiSelectOption[] = CivilSearchService.getIssueTypes();
  minYear = CivilSearchService.getCivilStartYear();
  maxYear = CivilSearchService.getCurrentYear();

  caseLookupResults: CourtCase[];
  private caseSearchResults: CivilIdentitySearchResponse;
  private judgmentSearchResults: CivilIdentitySearchResponse;

  showFieldCodeFlag = false;
  private caseCategory = 'Civil Actions';
  isSearchDivOpen = true;

  searchRecordLastUpdateDate$: Observable<String>;

  dateOptions = {  year: 'numeric', month: '2-digit', day: '2-digit' };
  dateForToday: string;
  minPartyEnteredDate = new Date(1966, 1, 1);
  maxPartyEnteredDate = new Date();

  // validation parameters and validators
  delayTime = 1000; // ms
  caseNumberRegExp: RegExp = new RegExp(/^\s*((?:19|20)?\d{2})?\s*(CRS|(CR[ ]?)|CVD|CVM|CVS|E|EO|J|M|R|SP)\s*(\d{1,6})\s*$/, 'i');
  yearRegExp: RegExp = new RegExp(/^\s*((19|20)\d{2})\s*$/, 'i');
  yearValidators = [Validators.pattern(/^\s*((19|20)\d{2})\s*$/i), Validators.min(this.minYear), Validators.max(this.maxYear)];
  // tslint:disable-next-line:max-line-length
  yearAsyncValidtors = [asyncMaxInt(this.maxYear, this.delayTime), asyncMinInt(this.minYear, this.delayTime), asyncPattern(this.yearRegExp, this.delayTime)];
  caseNumberValidators = [Validators.required, Validators.pattern(this.caseNumberRegExp)];
  caseNumberAsyncValidators = [asyncPattern(this.caseNumberRegExp, this.delayTime)];

  // party name search parameters and endpoint name
  partyNameSearchBaseParams: HttpParams;
  partyNameSearchEndpointName: string;

  private additionalListSource  = new Subject<CivilIdentitySearchCase[]>();
  private additonalListRequested$: Observable<CivilIdentitySearchCase[]> = this.additionalListSource.asObservable();

  constructor(private authenticationService: AuthenticationService,
              private civilSearchService: CivilSearchService,
              private eventService: EventService,
              private formBuilder: FormBuilder,
              private route: ActivatedRoute,
              private datePipe: DatePipe) {
      this.route.params.subscribe( params => {
        this.caseCategory = params['caseCategory'];
      });
      this.searchRecordLastUpdateDate$ = this.civilSearchService.getSearchRecordLastUpdateDate();
      this.dateForToday = new Date().toLocaleDateString('en-US', this.dateOptions);
  }

  ngOnInit() {
      this.civilSearchFormGroup = this.formBuilder.group({
          caseLookup: this.formBuilder.group({
            county: ['', Validators.required],
            caseNumber: ['', [Validators.required], this.caseNumberAsyncValidators]
          }),
          nameSearch: this.formBuilder.group({
            searchBusinessNames: [false, []],
            businessName: ['', []],
            firstName: ['', []],
            middleName: ['', []],
            lastName: ['', []],
            counties: [[], []],
            searchNameMatchType: ['Exact Match'],
            advancedDateFilter: [false, []],
            partyEnteredDate: ['', []],
            fromYear: [this.minYear, null, this.yearAsyncValidtors],
            toYear: [this.maxYear, null, this.yearAsyncValidtors],
            caseTypes: [this.getDefaultCaseTypes(), []],
            issueTypes: [[], []],
            casesPageNumber: [],
            indexedCasesPageNumber: []
          }, {validator: null,
             asyncValidator: [asyncFromGreater('fromYear', 'toYear', this.delayTime)]})
      });

      // conditional validation   -- business name is not required anymore (04/24 requirement changed - 05/23 code change)
      /*
      this.civilSearchFormGroup.get('nameSearch.searchBusinessNames').valueChanges.subscribe(
         (searchBusinessNames: boolean) => {
           if (searchBusinessNames) {
             this.civilSearchFormGroup.get('nameSearch.businessName').setValidators([Validators.required]);
           } else {
             this.civilSearchFormGroup.get('nameSearch.businessName').setValidators([]);
           }
           this.civilSearchFormGroup.get('nameSearch.businessName').updateValueAndValidity();
           this.civilSearchFormGroup.get('nameSearch.lastName').updateValueAndValidity();
      });
      */

      this.civilSearchFormGroup.get('caseLookup').disable();

      this.resetForms();
  }

  /**
   *
   */
  ngAfterViewChecked(): void {
    // get the tab link elements (note: id is derrived by ngx-bbotstrap)
    let caseTabLink: HTMLElement  = document.getElementById('case-party-tab-link');
    let judgTabLink: HTMLElement  = document.getElementById('judgment-index-tab-link');

    // set the tabindex attributes to the tab link elements
    if (caseTabLink &&  judgTabLink) {
      caseTabLink.setAttribute('tabindex', '491');
      judgTabLink.setAttribute('tabindex', '492');
    }
  }

  selectAccordion(type: AccordionType, event: any): void {

    const caseSearchAccordionHead: HTMLElement = document.getElementById('civil-search-criteria-overhead-search-by-case-number');
    const nameSearchAccordionHead: HTMLElement = document.getElementById('civil-search-criteria-overhead-search-by-other');

    if (type === this.openAccordion) {
      event.stopPropagation();

      // toggle to open another accordion and close this opened one
      if (type === AccordionType.Case_Number) {
        nameSearchAccordionHead.click();
      } else {
        caseSearchAccordionHead.click();
      }

      return;
    }

    // reset summitted status
    this.errors = [];
    this.submitted = false;

    // set accordion type and reset some values
    this.openAccordion = type;
    if (type === AccordionType.Case_Number) {
      this.civilSearchFormGroup.get('caseLookup').enable();
      this.civilSearchFormGroup.get('nameSearch').disable();
      this.eventService.focusField('civil-search-number-input');
    } else if (type === AccordionType.Party_Name) {
      this.activeSearchResultTab = PartyNameSearchResultTab.Cases;
      this.civilSearchFormGroup.get('caseLookup').disable();
      this.civilSearchFormGroup.get('nameSearch').enable();
      this.eventService.focusField('civil-last-name-input');
    }
  }

  selectMatchType(value: string) {
    const control: AbstractControl = this.civilSearchFormGroup.get('nameSearch.searchNameMatchType');
    control.setValue(value);
  }

  private invokeSearchService(searchType: SearchType): Observable<CivilCase | CivilIdentitySearchResponse> {

    switch (searchType) {
      case SearchType.Case_Lookup: {
        return this.exectuteCaseNumberLookup();
      }
      case SearchType.Party_Name_Cases: {
        this.resetPageNumberInPartyNameParameters(false);
        return this.civilSearchService.partyNameSearch(this.partyNameSearchBaseParams, this.partyNameSearchEndpointName);
        // return this.civilSearchService.partyNameSearch(this.civilSearchFormGroup.value.nameSearch, false);
      }
      case SearchType.Party_Name_Indexed_Judgments: {
        this.resetPageNumberInPartyNameParameters(true);
        return this.civilSearchService.partyNameSearch(this.partyNameSearchBaseParams, this.partyNameSearchEndpointName);
        // return this.civilSearchService.partyNameSearch(this.civilSearchFormGroup.value.nameSearch, true);
      }
    }
  }

  private exectuteCaseNumberLookup(): Observable<CivilCase> {
    const caseIdentifier = new CaseIdentifier(
      'CIVIL',
      this.civilSearchFormGroup.get('caseLookup.county').value as string,
      (this.civilSearchFormGroup.get('caseLookup.caseNumber').value + '').toUpperCase() // change to upper case
    );
    this.searchCriteriaDescription = 'County = "' + caseIdentifier.county + '" | Case Number = "' + caseIdentifier.caseNumber + '"';
    return this.civilSearchService.findByCaseIdentifier(caseIdentifier);
  }

  onSearch() {
    if (this.openAccordion === AccordionType.Case_Number) {
      this.activeSearchResultTab = null;
    } else {
      // party name search button default to case tab
      this.activeSearchResultTab = PartyNameSearchResultTab.Cases;
    }

    this.caseSearchResults = null;
    this.judgmentSearchResults = null;
    this.searchCriteriaDescription = null;
    this.resetPageNumbers();

    // build party name search base parameters(assigning new params and endpointname )
    if (this.activeSearchResultTab === PartyNameSearchResultTab.Cases) {
      this.buildPartyNameSearchBaseParameters();
    }

    // do the search
    this.search();
  }

  private resetPageNumbers() {
    this.civilSearchFormGroup.get('nameSearch.casesPageNumber').setValue(null);
    this.civilSearchFormGroup.get('nameSearch.indexedCasesPageNumber').setValue(null);
  }

  private search(gettingNext = false) {
    this.errors = [];
    this.caseLookupResults = null;

    if (this.requestSubscription) {
      this.requestSubscription.unsubscribe();
    }
    if (!gettingNext) {
      this.cancelAdditionalOutstandingRequests();
    }


    // TODO: debate if the above code should execute when submitting an invalid form
    this.submitted = true;

    if (this.civilSearchFormGroup.invalid) {
        return;
    }
    this.submitted = false; // TODO: is this the best way to manage the submitted flag?


    if (this.openAccordion === AccordionType.Case_Number && !this.activeSearchResultTab) {
      this.lastSubmittedSearchType = SearchType.Case_Lookup;
      this.activeSearchResultTab = null;
    } else {
      this.searchCriteriaDescription = CivilSearchService.getPartySearchCriteriaDescription(this.civilSearchFormGroup.value.nameSearch);
      if (this.activeSearchResultTab === PartyNameSearchResultTab.Cases) {
        this.lastSubmittedSearchType = SearchType.Party_Name_Cases;
      } else if (this.activeSearchResultTab === PartyNameSearchResultTab.Indexed_Judgments) {
        this.lastSubmittedSearchType = SearchType.Party_Name_Indexed_Judgments;
      }
    }

    this.requestSubscription = this.invokeSearchService(this.lastSubmittedSearchType)
        .subscribe(searchResults => {
          switch (this.lastSubmittedSearchType) {
            case SearchType.Party_Name_Cases:
            case SearchType.Party_Name_Indexed_Judgments: {
              this.handleTabbedSearchResult(searchResults as CivilIdentitySearchResponse);
              break;
            }
            case SearchType.Case_Lookup: {
              this.caseLookupResults = [searchResults as CivilCase];
              break;
            }
            default: {
              console.error('Unknown search type encountered "' + this.lastSubmittedSearchType + '".');
              break;
            }
          }
        },
        (errorMessage: string) => this.errors.push(errorMessage));



    this.scrollToTop();
  }

  private scrollToTop(): void {
    // when clicking on search button for party name search
    if (this.activeSearchResultTab === PartyNameSearchResultTab.Cases
       && !this.caseSearchResults
       && this.errors) {
      document.documentElement.scrollTop = 0;
    }
  }

  private resetForms() {
    this.civilSearchFormGroup.reset();

    this.civilSearchFormGroup.get('nameSearch.searchNameMatchType').setValue('Exact Match');
    this.civilSearchFormGroup.get('nameSearch.fromYear').setValue(this.minYear);
    this.civilSearchFormGroup.get('nameSearch.toYear').setValue(this.maxYear);
    this.civilSearchFormGroup.get('nameSearch.partyEnteredDate').setValue('');
    this.resetPageNumbers();
    this.civilSearchFormGroup.get('nameSearch.caseTypes').setValue(this.getDefaultCaseTypes());

    const userCounty = this.authenticationService.getCurrentUser().authorization.geoName;
    this.civilSearchFormGroup.get('caseLookup.county').setValue(userCounty);
    this.civilSearchFormGroup.get('nameSearch.counties').setValue([userCounty]);

    if (this.openAccordion === AccordionType.Case_Number) {
      // set default focus
      this.eventService.focusField('civil-search-number-input');
    } else {
      // set default focus
      this.eventService.focusField('civil-last-name-input');

      // set default tab if it is not set
      if (!this.activeSearchResultTab) {
        this.activeSearchResultTab = PartyNameSearchResultTab.Cases;
      }
    }
  }

  private handleTabbedSearchResult(result: CivilIdentitySearchResponse): void {
    let querySequenceType: QuerySequenceType = null;
    querySequenceType = QuerySequenceType.IN_MAP_CALL;
    // querySequenceType = QuerySequenceType.SEQ_PREV_INTERVAL;
    // querySequenceType = QuerySequenceType.IN_ONE_CALL;
    // querySequenceType = QuerySequenceType.SEQ_PREV_COMPLETED;

    if (this.activeSearchResultTab === PartyNameSearchResultTab.Cases) {
      let newResult = this.cloneCivilIdentitySearchResponse(result)
      if (newResult.pageNumber > 1) {
        newResult.cases.unshift(...this.caseSearchResults.cases);
      }
      this.caseSearchResults = newResult;

       // --- OtherData: additonal request - combined solution
       this.requestOtherDataForCasesByDividedBatch(result.cases, false, querySequenceType);

      // --- Other Data: additonal request - first solution
      // this.requestOtherDataForCasesInBatch(result.cases, false);

      /* --- Other data; original code ----
      this.requestOtherDataForCases(result.cases, false);
      if (result.pageNumber > 1) {
        result.cases.unshift(...this.caseSearchResults.cases);
      }
      this.caseSearchResults = result;
      */
    } else if (this.activeSearchResultTab === PartyNameSearchResultTab.Indexed_Judgments) {
      // concatinate all results
      let newResult = this.cloneCivilIdentitySearchResponse(result)
      if (newResult.pageNumber > 1) {
        newResult.cases.unshift(...this.judgmentSearchResults.cases);
      }
      this.judgmentSearchResults = newResult;

      // --- Other Data additional request - combined solution
      this.requestOtherDataForCasesByDividedBatch(result.cases, true, querySequenceType);

      // --- Other Data: additonal request - first solution
      // this.requestOtherDataForCasesInBatch(result.cases, true);

      /* --- Other data; original code ----
      this.requestOtherDataForCases(result.cases, true);
      if (result.pageNumber > 1) {
        result.cases.unshift(...this.judgmentSearchResults.cases);
      }
      this.judgmentSearchResults = result;
      */
    }
  };

  private cancelAdditionalOutstandingRequests() {
    if (this.otherDataRequestSubscription) {
      this.otherDataRequestSubscription.unsubscribe();
    }
  }

  private requestOtherDataForCases(cases: CivilIdentitySearchCase[], indexedJudgmentOnly: boolean) {
    this.otherDataRequestSubscription = Observable.from<CivilIdentitySearchCase>(cases)
    .flatMap(acase => this.getOtherData(acase, indexedJudgmentOnly), (a, b) => { return {case: a, other: b}; }, 5)
    .subscribe(data => {
      data.case.otherData = data.other;
      this.eventService.otherDataLoaded();
    });
  }

  private getOtherData(acase: CivilIdentitySearchCase, indexedJudgmentOnly: boolean) {
    return this.civilSearchService.searchOtherData(acase, indexedJudgmentOnly);
  }

  private requestOtherDataForCasesInBatch(cases: CivilIdentitySearchCase[], indexedJudgmentOnly: boolean) {
    this.otherDataRequestSubscription = this.civilSearchService.searchPartyAdditionalData(cases, indexedJudgmentOnly)
    .subscribe(map => {
       this.setOtherDataInBatch(cases, indexedJudgmentOnly, map);
       this.eventService.otherDataLoaded();
    });
  }

  /**
   * This method will retrieve the additional data by divided the request groups in subgroups.
   * TODO: removed unused options after initial test is done.
   *
   * @param cases
   * @param indexedJudgmentOnly
   * @param querySequenceType
   */
  private requestOtherDataForCasesByDividedBatch(cases: CivilIdentitySearchCase[],
    indexedJudgmentOnly: boolean, querySequenceType: QuerySequenceType ) {
    let searchResultType = indexedJudgmentOnly ? 'Idxed' : 'Cases';

    // define grouped sized and subgrouped arrays
    let allRequestData: any[] = this.civilSearchService.buildAdditionalRequestData(cases, indexedJudgmentOnly);
    let groupedSize = 5;
    if (allRequestData.length < groupedSize ) {
      groupedSize = allRequestData.length;
    }
    let groupedCount = Math.ceil(allRequestData.length / (groupedSize * 1.0));
    let groupedRequestData = new Array(groupedCount).fill('')
           .map(function() { return this.splice(0, groupedSize) }, allRequestData.slice());
    const sequentialInterval = 5; // ms - the interval time if calling by given intervals
    const mapCount =  groupedCount;
    if (!environment.production) {
      console.log('All request data = ');
      console.log(allRequestData);
      console.log('grouped data = ');
      console.log(groupedRequestData);
    }

    // start qierying based on query type
    const startTime = Date.now();
    if ( querySequenceType ===  QuerySequenceType.IN_ONE_CALL || groupedCount === 1) {
        this.otherDataRequestSubscription = this.civilSearchService.searchPartyAdditionalDataWithJsonRequest(allRequestData)
          .subscribe( map => {
              this.setOtherDataInBatch(cases, indexedJudgmentOnly, map);
              this.eventService.otherDataLoaded();
              if (!environment.production) {
                console.log('Result Type: ' + searchResultType + '|SequenceType: ' + querySequenceType
                + ':: All--- Addtional Response Elapsed Time = ' + (Date.now() - startTime) );
              }
          });
      } else {
         if (querySequenceType === QuerySequenceType.IN_MAP_CALL) {
           this.otherDataRequestSubscription = Observable.from<any>(groupedRequestData)
            .flatMap(subRequestData => this.civilSearchService.searchPartyAdditionalDataWithJsonRequest(subRequestData), mapCount)
            .subscribe(map => {
               this.setOtherDataInBatch(cases, indexedJudgmentOnly, map);
               this.eventService.otherDataLoaded();
               if (!environment.production && this.isAllOtherDataLoaded(cases)) {
                 console.log('Result Type: ' + searchResultType + '|Sequence Type: ' + querySequenceType
                 + '| groupSize= ' + groupedSize + '|groupedCount = ' + groupedCount  + '|mapCount = ' + mapCount
                 + ':: All--- Addtional Response Elapsed Time = ' + (Date.now() - startTime) );
               }
            });
         } else if (querySequenceType === QuerySequenceType.SEQ_PREV_INTERVAL) {
            groupedRequestData.forEach((subRequestData, index) => {
              setTimeout(() => { this.otherDataRequestSubscription =
              this.civilSearchService.searchPartyAdditionalDataWithJsonRequest(subRequestData)
                  .subscribe(map => {
                   this.setOtherDataInBatch(cases, indexedJudgmentOnly, map);
                   this.eventService.otherDataLoaded();
                   if (!environment.production && this.isAllOtherDataLoaded(cases)) {
                      console.log('Result Type: ' + searchResultType + '|SequenceType: ' + querySequenceType
                      + '| groupSize= ' + groupedSize + '|groupedCount = ' + groupedCount + '|Interval = ' + sequentialInterval + '(ms)'
                      + ':: All--- Addtional Response Elapsed Time = ' + (Date.now() - startTime) );
                   }
                });
             }, (index === 0 ? 0 : sequentialInterval));
           });
         } else if (querySequenceType === QuerySequenceType.SEQ_PREV_COMPLETED) {
          this.otherDataRequestSubscription = this.requestOtherDataForCasesUponCompletedRecursive(cases, indexedJudgmentOnly,
              groupedRequestData, 0, startTime, groupedCount).subscribe(x => {
                if (!environment.production ) {
                  console.log('Recursive load is done: ' + x );
                }
              })
         }
      }
  }
  private requestOtherDataForCasesUponCompletedRecursive(cases: CivilIdentitySearchCase[], indexedJudgmentOnly: boolean,
     groupedRequestData: any[], index: number, startTime: any, groupedCount: number): Observable<boolean> {
      let searchResultType = indexedJudgmentOnly ? 'Indexed' : 'Cases';
     if (index < groupedCount) {
        this.civilSearchService.searchPartyAdditionalDataWithJsonRequest(groupedRequestData[index])
        .subscribe(map => {
           this.setOtherDataInBatch(cases, indexedJudgmentOnly, map);
           this.eventService.otherDataLoaded();
           if (this.isAllOtherDataLoaded(cases)) {
              if (!environment.production) {
                console.log('Result Type: ' + searchResultType + '|SequenceType: ' + QuerySequenceType.SEQ_PREV_COMPLETED
                + ':: All--- Addtional Response Elapsed Time = ' + (Date.now() - startTime) );
              }
              return Observable.of<boolean>(true);
           } else {
             return  this.requestOtherDataForCasesUponCompletedRecursive(cases,  indexedJudgmentOnly,
                groupedRequestData, (index + 1), startTime, groupedCount);
           }
        });
      }

      return Observable.of<boolean>(true);
  }

  onStartOver() {
    this.errors = [];
    this.resetForms();
    if (this.requestSubscription) {
      this.requestSubscription.unsubscribe();
    }

    this.cancelAdditionalOutstandingRequests();
    this.submitted = false;

    this.caseLookupResults = null;
    this.caseSearchResults = null;
    this.judgmentSearchResults = null;
    this.searchCriteriaDescription = null;
    this.lastSubmittedSearchType = null;
  }

  tabSelected(tabName: PartyNameSearchResultTab) {
    // open the party name accordion if it is closed
    if (this.isCaseNumberAccordionOpen()) {
      const partyNameAccordion = document.getElementById('civil-search-criteria-overhead-search-by-other');
      partyNameAccordion.click();
    }

    // do the corresponding search
    this.activeSearchResultTab = tabName;
    this.errors = [];
    console.log(tabName + ' selected');
    if ((tabName === PartyNameSearchResultTab.Indexed_Judgments && !this.judgmentSearchResults) ||
        (tabName === PartyNameSearchResultTab.Cases && !this.caseSearchResults)) {

      // reset all data
      this.searchCriteriaDescription = null;
      this.resetPageNumbers();

      // do search
      this.search();

    } else if ((tabName === PartyNameSearchResultTab.Indexed_Judgments && this.judgmentSearchResults) ||
           (tabName === PartyNameSearchResultTab.Cases && this.caseSearchResults )) {
      // reload unfinished other data (opposing party or in Regards to) when switching back tab
      // -- get all the search results
      const allResults: CivilIdentitySearchCase[] =
                       (tabName === PartyNameSearchResultTab.Cases) ? this.caseSearchResults.cases : this.judgmentSearchResults.cases;
      const unfinishedResults = allResults.filter(result => !result.isOtherDataPresent());

      // -- loading the other data data for all unfinished cases
      if (unfinishedResults.length > 0) {
        const indexedJudgmentOnly = tabName === PartyNameSearchResultTab.Indexed_Judgments;
        // this.requestOtherDataForCases(unfinishedResults, indexedJudgmentOnly);
        this.requestOtherDataForCasesInBatch(unfinishedResults, indexedJudgmentOnly)
      }
    }
  }

  openDisclaimer() {
    console.log('access the opening disclainer');
    this.eventService.openDisclaimer();
  }

   /**
     * Generate display value representing the selected multi-select dropdown values.
     * @param {string} type - 'counties', 'caseTypes' or 'issueTypes'
     * @return {string} Formatted string representing the selected dropdown values.
     */
  getSelectedNameSearchValues(type: string): string {
    const nameSearchValueArray = this.getSelectedNameSearchValueArray(type);
    return nameSearchValueArray ? nameSearchValueArray.join(', ') : 'ALL';
  }

  /**
    * Generate display value representing the selected multi-select dropdown values.
    * @param {string} type - 'counties', 'caseTypes' or 'issueTypes'
    * @return {string[]} Formatted string array representing the selected dropdown values.
    */
  getSelectedNameSearchValueArray(type: string): string[] {
    // Note: this code depends on the naming pattern of the form controls and the instance variable lists
    let result: string[] = null;
    const selectedDropdownIds: string[] = this.civilSearchFormGroup.get('nameSearch.' + type).value;
    if (selectedDropdownIds && selectedDropdownIds.length && selectedDropdownIds.length < this[type].length) {
      const selectedIMultiSelectOptions = this[type].filter(v => selectedDropdownIds.indexOf(v.id) !== -1);
      const selectedDropdownNames: string[] = selectedIMultiSelectOptions.map(o => o.name);
      result = selectedDropdownNames;
    }
    return result;
  }

  getIdentitySearchCases(): CourtCase[] {
    return this.caseSearchResults && this.caseSearchResults.cases;
  }
  getIdentitySearchIndexedCases(): CourtCase[] {
    return this.judgmentSearchResults && this.judgmentSearchResults.cases;
  }
  doesIdentitySearchCasesHaveMoreRecords(): boolean {
    return !!this.caseSearchResults &&
        (this.caseSearchResults.totalRecords >
         this.caseSearchResults.pageNumber * this.caseSearchResults.pageSize);
  }
  doesIdentitySearchIndexedCasesHaveMoreRecords(): boolean {
    return !!this.judgmentSearchResults &&
        (this.judgmentSearchResults.totalRecords >
         this.judgmentSearchResults.pageNumber * this.judgmentSearchResults.pageSize);
  }

  getNextIdentitySearchCases() {
    this.searchCriteriaDescription = null;
    this.civilSearchFormGroup.get('nameSearch.casesPageNumber').setValue(this.caseSearchResults.pageNumber + 1);
    this.search(true);
  }
  getNextIdentitySearchIndexedCases() {
    this.searchCriteriaDescription = null;
    this.civilSearchFormGroup.get('nameSearch.indexedCasesPageNumber').setValue(this.judgmentSearchResults.pageNumber + 1);
    this.search(true);
  }

  getTotalRecordsDescription(): string {
    const relevantResults =
      this.activeSearchResultTab === PartyNameSearchResultTab.Cases ? this.caseSearchResults : this.judgmentSearchResults,
          relevantResultsType = this.activeSearchResultTab === PartyNameSearchResultTab.Cases ? 'cases' : 'indexed judgments';

    if (!relevantResults || !relevantResults.totalRecords) {
      return '';
    }
    return (relevantResults.totalRecords).toLocaleString() + ' ' + relevantResultsType + ' found';
  }

  getDisplayedRecordsDescription(): string {
    const relevantResults =
      this.activeSearchResultTab === PartyNameSearchResultTab.Cases ? this.caseSearchResults : this.judgmentSearchResults,
    relevantResultsType = this.activeSearchResultTab === PartyNameSearchResultTab.Cases ? 'case records' : 'indexed judgment records';

    if (!relevantResults || !relevantResults.totalRecords) {
      return '';
    }
    return (relevantResults.pageNumber * CivilSearchConstants.PAGE_SIZE).toLocaleString()
    + ' of ' + (relevantResults.totalRecords).toLocaleString()
    + ' ' + relevantResultsType + ' are being displayed. ';
  }

  /**
   * validator for checking if the from year is greater than through year
   * @param group
   */
  isFromYearGreater(group: FormGroup) {
    const fromYear: AbstractControl = group.get('fromYear');
    const toYear: AbstractControl = group.get('toYear');
    if (!fromYear || !toYear || !fromYear.value || !toYear.value) {
      return null;
    }
    if (fromYear.value <= toYear.value) {
      return null;
    }
    return {fromGreater: true};
  }

  toggleFieldCodes() {
    this.showFieldCodeFlag = !this.showFieldCodeFlag;
    $('.field-code').toggle();
  }

  /**
   * get the default case types ( code array) for the selected record type e.g. Civil Actions, Estates, etc.
   */
  private getDefaultCaseTypes() {
    const defaultOptions: IMultiSelectOption[] = CivilSearchService.getCaseTypes(this.caseCategory);
    const defaultCaseTypes: string[] = defaultOptions.map(o => o.id);
    return defaultCaseTypes;
  }

  isProd(): boolean {
    return environment.production;
  }

  /**
   * toggle to open/close search criteria div
   */
  toggleSearchCriteria() {
    const searchDiv = document.getElementById('civil-search-criteria-div');
    const searchDivWidth = searchDiv.clientWidth;
    const resultDiv = document.getElementById('civil-search-results-div');
    const resultDivOriginPaddingLeft = 15;
    const resultDivChangePaddingLeft = 35;  // on closing search div

    // before closing search div, set search div transition and reset result div's left padding
    searchDiv.style.transition = 'all .6s';
    if (this.isSearchDivOpen) {
      resultDiv.style.paddingLeft = (searchDivWidth + resultDivOriginPaddingLeft ) + 'px';
    }

    // toggle to open/close search div and reset result div's padding left with transition
    this.isSearchDivOpen = !this.isSearchDivOpen;
    if (!this.isSearchDivOpen) { // search div being closed
       setTimeout(() => {
          resultDiv.style.paddingLeft = resultDivChangePaddingLeft + 'px';
          resultDiv.style.transition = 'all .6s';
       }, 10);
    } else {                     // search div beig opened
       resultDiv.style.paddingLeft = resultDivOriginPaddingLeft  + 'px';
       resultDiv.style.transition = 'all 0s';
    }
  }

  /**
   * check if the case number accordion is open
   */
  isCaseNumberAccordionOpen(): boolean {
    return this.openAccordion === AccordionType.Case_Number;
  }

  isRequestInFlight(): boolean {
    return this.requestSubscription &&
           !this.requestSubscription.closed;
  }

  private buildIMultiSelectOptions(items: string[]): IMultiSelectOption[] {
    return items.map(i => { return {'id': i, 'name': i }; });
  }
  setToday() {
    this.civilSearchFormGroup.get('nameSearch.partyEnteredDate').setValue(this.dateForToday);
  }
  /**
   * Due to Angular validation has less access points for setting validators for different event in
   * the validator configuration (still seeking the solution in this way),  Meanwhile, just reset
   * validators and update validity for the form controls as well as the form group (if necessary)
   * to achieve the validation requirement specifications.
   *
   * @param $event Event
   * @param formControlName: string
   * @param formName: string
   */
  updateValidity($event, formControlName: string, formName: string) {
    if ($event.type === 'blur') {
      // on blur - instant validation
      this.civilSearchFormGroup.get(formControlName).clearAsyncValidators();
      if (formControlName.indexOf('caseNumber') >= 0) {
        this.civilSearchFormGroup.get(formControlName).setValidators(this.caseNumberValidators);
      } else if (formControlName.indexOf('fromYear') >= 0 || formControlName.indexOf('toYear') >= 0) {
        this.civilSearchFormGroup.get(formControlName).setValidators(this.yearValidators);
        this.civilSearchFormGroup.get(formName).setAsyncValidators(null);
        this.civilSearchFormGroup.get(formName).setValidators([this.isFromYearGreater]);
      }
      this.civilSearchFormGroup.get(formControlName).updateValueAndValidity();
    } else if ($event.type === 'focus') {
      // on focus -instant validation if initial value entry exists upon focus, else asychrous validation
      if (this.civilSearchFormGroup.get(formControlName).value !== null
          && this.civilSearchFormGroup.get(formControlName).value !== undefined
          && this.civilSearchFormGroup.get(formControlName).value.length > 0 ) {
        this.civilSearchFormGroup.get(formControlName).clearAsyncValidators();
        if (formControlName.indexOf('caseNumber') >= 0) {
          this.civilSearchFormGroup.get(formControlName).setValidators(this.caseNumberValidators);
        } else if (formControlName.indexOf('fromYear') >= 0 || formControlName.indexOf('toYear') >= 0) {
          this.civilSearchFormGroup.get(formControlName).setValidators(this.yearValidators);
          // set form validation to asynch
          this.civilSearchFormGroup.get(formName).setAsyncValidators([asyncFromGreater('fromYear', 'toYear', this.delayTime)]);
          this.civilSearchFormGroup.get(formName).setValidators(null);
        }
        this.civilSearchFormGroup.get(formControlName).updateValueAndValidity();
      } else {
        this.civilSearchFormGroup.get(formControlName).clearValidators();
        if (formControlName.indexOf('caseNumber') >= 0) {
          this.civilSearchFormGroup.get(formControlName).setValidators([Validators.required]);
          this.civilSearchFormGroup.get(formControlName).setAsyncValidators(this.caseNumberAsyncValidators);
        } else if (formControlName.indexOf('fromYear') >= 0 || formControlName.indexOf('toYear') >= 0) {
          this.civilSearchFormGroup.get(formControlName).setAsyncValidators(this.yearAsyncValidtors);
           // set form validation to asynch
           this.civilSearchFormGroup.get(formName).setAsyncValidators([asyncFromGreater('fromYear', 'toYear', this.delayTime)]);
           this.civilSearchFormGroup.get(formName).setValidators(null);
        }
        this.civilSearchFormGroup.get(formControlName).updateValueAndValidity();
      }
    } else if ($event.type === 'keypress') {
      let keyCode = (typeof $event.which === 'number') ? $event.which : $event.keyCode;
      // key press - instant validation for enter key;
      //             instant validation for initial value exists and invalid status upon key press;
      //             asynchrous validation for initial value empty or valid status upon key press
      //                                     (stauts may be continuously updated in asynchrous validation)
      if (keyCode === 13) {  // key press - enter key
        this.civilSearchFormGroup.get(formControlName).clearAsyncValidators();
        if (formControlName.indexOf('caseNumber') >= 0) {
          this.civilSearchFormGroup.get(formControlName).setValidators(this.caseNumberValidators);
        } else if (formControlName.indexOf('fromYear') >= 0 || formControlName.indexOf('toYear') >= 0) {
          this.civilSearchFormGroup.get(formControlName).setValidators(this.yearValidators);
          this.civilSearchFormGroup.get(formName).setAsyncValidators(null);
          this.civilSearchFormGroup.get(formName).setValidators([this.isFromYearGreater]);
        }
        this.civilSearchFormGroup.get(formControlName).updateValueAndValidity();
      } else {                // key press - any other key
        if (this.civilSearchFormGroup.get(formControlName).value !== null
            && this.civilSearchFormGroup.get(formControlName).value !== undefined
            && this.civilSearchFormGroup.get(formControlName).value.length > 1
            && this.civilSearchFormGroup.get(formControlName).invalid) {
          this.civilSearchFormGroup.get(formControlName).clearAsyncValidators();
          if (formControlName.indexOf('caseNumber') >= 0) {
             this.civilSearchFormGroup.get(formControlName).setValidators(this.caseNumberValidators);
          } else if (formControlName.indexOf('fromYear') >= 0 || formControlName.indexOf('toYear') >= 0) {
            this.civilSearchFormGroup.get(formControlName).setValidators(this.yearValidators);
             // set form validation to asynch
             this.civilSearchFormGroup.get(formName).setAsyncValidators([asyncFromGreater('fromYear', 'toYear', this.delayTime)]);
             this.civilSearchFormGroup.get(formName).setValidators(null);
          }
           this.civilSearchFormGroup.get(formControlName).updateValueAndValidity();
        } else {
          this.civilSearchFormGroup.get(formControlName).clearValidators();
          if (formControlName.indexOf('caseNumber') >= 0) {
            this.civilSearchFormGroup.get(formControlName).setValidators([Validators.required]);
            this.civilSearchFormGroup.get(formControlName).setAsyncValidators(this.caseNumberAsyncValidators);
          } else if (formControlName.indexOf('fromYear') >= 0 || formControlName.indexOf('toYear') >= 0) {
            this.civilSearchFormGroup.get(formControlName).setAsyncValidators(this.yearAsyncValidtors);
             // set form validation to asynch
            this.civilSearchFormGroup.get(formName).setAsyncValidators([asyncFromGreater('fromYear', 'toYear', this.delayTime)]);
            this.civilSearchFormGroup.get(formName).setValidators(null);
          }
          this.civilSearchFormGroup.get(formControlName).updateValueAndValidity();
        }
      }
    }
  }

  ngOnDestroy() {
    this.cancelAdditionalOutstandingRequests();
  }

  /**
   * buid the party name search base parameters and endpoint name
   * at the search button being clicked when doing party search
   */
  private buildPartyNameSearchBaseParameters(): void {
    // console.log("calling build parameters");
    let endpointName;
    let params = new HttpParams();
    const searchForm = this.civilSearchFormGroup.value.nameSearch;

    const matchType = CivilSearchService.translateMatchType(searchForm.searchNameMatchType);
    if (searchForm.searchBusinessNames && searchForm.businessName) {
      endpointName = 'business/search';
      params = params.set('name', CivilSearchService.normalizeNameInput(searchForm.businessName));
      params = params.set('match_type', matchType);
    } else if (CivilSearchService.normalizeNameInput(searchForm.lastName)
              || CivilSearchService.normalizeNameInput(searchForm.firstName)
              || CivilSearchService.normalizeNameInput(searchForm.middleName) ) {

      endpointName = 'person/search';
      if (searchForm.lastName) {
        params = params.set('last_name', CivilSearchService.normalizeNameInput(searchForm.lastName));
        params = params.set('ln_match_type', matchType);
      }

      if (searchForm.firstName) {
        params = params.set('first_name', CivilSearchService.normalizeNameInput(searchForm.firstName));
        params = params.set('fn_match_type', matchType);
      }

      if (searchForm.middleName) {
        params = params.set('middle_name', CivilSearchService.normalizeNameInput(searchForm.middleName));
        params = params.set('mn_match_type', matchType);
      }
    } else {
      endpointName = 'search'; // endpoint for person first name, last name and middle name empty , Or business name empty
    }


    if (searchForm.counties &&
        searchForm.counties.length &&
        searchForm.counties.length < CountyService.number_of_counties) {
          params = params.set('counties', searchForm.counties.join(','));
    }

    // Case Years - from and to
    if (searchForm.advancedDateFilter) {
       if (searchForm.partyEnteredDate) {
        params = params.set('date_entered', CivilSearchService.convertDateToString(searchForm.partyEnteredDate));
       }
    } else {
      const fromYear = searchForm.fromYear || CivilSearchService.getCivilStartYear();
      const toYear = searchForm.toYear || CivilSearchService.getCurrentYear();
      params = params.set('years', fromYear + ',' + toYear);
    }

    // Case types
    if (searchForm.caseTypes && searchForm.caseTypes.length && searchForm.caseTypes.length < CivilSearchService.getCaseTypes(null).length) {
      params = params.set('court_types', searchForm.caseTypes.join(','));
    }

    // Issue types
    if (searchForm.issueTypes && searchForm.issueTypes.length && searchForm.issueTypes.length < CivilSearchService.getIssueTypes().length) {
      params = params.set('issue_types', searchForm.issueTypes.join(','));
    }

     // set to local variables
     this.partyNameSearchEndpointName = endpointName;
     this.partyNameSearchBaseParams = params;
  }

  /**
   * reset page number and page size for different search type (cases or indexed judgements)
   *
   * @param indexedJudgmentsOnly
   */
  private resetPageNumberInPartyNameParameters(indexedJudgmentsOnly: boolean) {
    const searchForm = this.civilSearchFormGroup.value.nameSearch;
    const pageNumber = (indexedJudgmentsOnly ? searchForm.indexedCasesPageNumber : searchForm.casesPageNumber) || 1;
    this.partyNameSearchBaseParams = this.partyNameSearchBaseParams.set('page', pageNumber);
    this.partyNameSearchBaseParams = this.partyNameSearchBaseParams.set('size', CivilSearchConstants.PAGE_SIZE.toString());
    if (indexedJudgmentsOnly) {
      this.partyNameSearchBaseParams = this.partyNameSearchBaseParams.set('judgment', indexedJudgmentsOnly.toString());
    } else {
      this.partyNameSearchBaseParams = this.partyNameSearchBaseParams.delete('judgment');
    }
  }

  /**
   * set other data in bacth
   * @param civilIdentitySearchCaseList
   * @param indexedJudgmentOnly
   * @param additionalMap
   */
  setOtherDataInBatch(civilIdentitySearchCaseList: CivilIdentitySearchCase[],
    indexedJudgmentOnly: boolean, additionalMap: Map<string, CivilIdentityAdditionalData>) {
    /// if (!environment.production) {
    //   console.log('cases = ');
    //   console.log(civilIdentitySearchCaseList);
    //   console.log(' Map data = ' );
    //   console.log(additionalMap);
    // }
    // check if error occurs for the batch query
    let errorForAll = false;
    let errorAdditionalData: CivilIdentityAdditionalData = null;
    if (additionalMap.size === 1 && additionalMap.keys().next().value === 'error' ) {
      errorForAll = true;
      errorAdditionalData = additionalMap.values().next().value;
    }

    // set other data for each case
    for (let civilIdentitySearchCase of civilIdentitySearchCaseList)  {
      if (errorForAll) {
        let otherData: CivilIdentityOtherData = new CivilIdentityOtherData(null);
        otherData.errorMessage = errorAdditionalData.errorMessages ;
        civilIdentitySearchCase.otherData = otherData;
      } else {
        let realCaseNumber = FileNumberUtil.stripMiddleZeros(civilIdentitySearchCase.getRealCaseNumber().trim());
        let additionalData: CivilIdentityAdditionalData =  additionalMap.get(realCaseNumber);
        // if (!environment.production) {
        //  console.log('real number = ' +  realCaseNumber + '  and its case addiotional data = ');
        //  console.log(additionalData);
        // }
        // set the case row's other data corresponding to each details
        if (additionalData !== null &&  additionalData !== undefined)  { // addtional data is loaded
          for (let additionDetails  of additionalData.additionalPartyDetailsList) {
            let details = additionDetails as CivilIdentiyAdditionalDetails;
            if (this.isAddtionalDetailsMatched(civilIdentitySearchCase, details)) {
              let otherJson: any = indexedJudgmentOnly ? details.getIndexedJudgment() : details.getInRegardsTo();
              let otherData: CivilIdentityOtherData = new CivilIdentityOtherData(otherJson);
              otherData.errorMessage = details.getMessages();
              civilIdentitySearchCase.otherData = otherData;
              this.eventService.otherDataLoaded();
            }
          }
        }
      }
    }
  }

  /**
   * check if the details property matches
   * @param civilIdentitySearchCase
   * @param civilIdentityAdditionalDetails
   */
  private isAddtionalDetailsMatched(civilIdentitySearchCase: CivilIdentitySearchCase,
          civilIdentityAdditionalDetails: CivilIdentiyAdditionalDetails) {
     // tslint:disable-next-line:curly
     if (civilIdentitySearchCase
        && civilIdentityAdditionalDetails
        && (civilIdentitySearchCase.getPartyRoleCode().trim() === civilIdentityAdditionalDetails.getPartyRole().trim())
        && (civilIdentitySearchCase.getPartyNumber().toString() === civilIdentityAdditionalDetails.getPartyNumber().toString())
        && (civilIdentitySearchCase.getPartyEntryTime().trim() === civilIdentityAdditionalDetails.getPartyEntryTime().trim())
        && (civilIdentitySearchCase.getRowOrigin().trim() === civilIdentityAdditionalDetails.getNameType().trim())
        && (civilIdentitySearchCase.getOtherEntryTime().trim() === civilIdentityAdditionalDetails.getOtherEntryTime().trim())) {
         return true;
     }
     return false;
  }

  /**
   * checked if all the identity search cases have the other data loaded
   * @param civilIdentitySearchCaseList
   */
  private isAllOtherDataLoaded(civilIdentitySearchCaseList: CivilIdentitySearchCase[]) {
    // set other data for each case
    for (let civilIdentitySearchCase of civilIdentitySearchCaseList)  {
      if (!civilIdentitySearchCase.otherData) {
          return false;    // any one is not loaded
      }
    }

    return true;
  }

  /**
   * clone the response
   * @param result
   */
  private cloneCivilIdentitySearchResponse(result: CivilIdentitySearchResponse): CivilIdentitySearchResponse {
    let newResult = new CivilIdentitySearchResponse();
    newResult.cases = result.cases.map (x => x);
    newResult.pageNumber = result.pageNumber;
    newResult.pageSize = result.pageSize;
    newResult.totalRecords = result.totalRecords;
    return newResult;
  }
}

export enum QuerySequenceType {
  IN_ONE_CALL = 'in-one-call',
  IN_MAP_CALL = 'in-map-call',
  SEQ_PREV_INTERVAL = 'sequential-after-previous-with-interval',
  SEQ_PREV_COMPLETED = 'sequential-after-previous-compleneted'
};



// WEBPACK FOOTER //
// /home/jenkins/workspace/pipeline-ciprs-ui-final/project/src/app/civil/civil.component.ts